import { Box, Typography } from '@mui/material'
import { exportLeadTimeToCsv, useGetLeadTimeData } from '@src/api/leadtime-api'
import PageContainer from '@src/components/Layout/PageContainer'
import PageHeader from '@src/components/Layout/PageHeader'
import LeadTimeChart from '@src/components/LeadTime/LeadTimeChart'
import LeadTimeDataTable from '@src/components/LeadTime/LeadTimeDataTable'
import LeadTimeFilters from '@src/components/LeadTime/LeadTimeFilters'
import LeadTimeStatistics from '@src/components/LeadTime/LeadTimeStatistics'
import PageLoader from '@src/components/Loader/PageLoader'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const LeadTimePage = () => {
  const { t } = useTranslation()

  // Helper function to subtract months
  const subtractMonths = (date: Date, months: number): Date => {
    const result = new Date(date)
    result.setMonth(result.getMonth() - months)
    return result
  }

  // Default: last 12 months
  const [startDate, setStartDate] = useState<Date | null>(subtractMonths(new Date(), 12))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [shouldFetch, setShouldFetch] = useState<boolean>(true)

  const [data, isFetching] = useGetLeadTimeData(
    {
      startDate: startDate?.toISOString() || '',
      endDate: endDate?.toISOString() || '',
    },
    shouldFetch && !!startDate && !!endDate,
  )

  const handleRefresh = () => {
    setShouldFetch(false)
    setTimeout(() => setShouldFetch(true), 0)
  }

  const handleExport = () => {
    if (data?.records) {
      exportLeadTimeToCsv(
        data.records,
        startDate?.toISOString().split('T')[0] || '',
        endDate?.toISOString().split('T')[0] || '',
      )
    }
  }

  if (isFetching) {
    return <PageLoader />
  }

  return (
    <>
      <PageHeader
        title={t('breadcrumbs.leadTime')}
        breadcrumbs={{
          path: [{ href: '/leadtime', label: t('breadcrumbs.leadTime') }],
        }}
      />
      <PageContainer sx={{ padding: 2, flexGrow: 1, overflow: 'auto' }}>
        <LeadTimeFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onRefresh={handleRefresh}
          onExport={handleExport}
          isLoading={isFetching}
        />

        {data && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
              <Box sx={{ flex: 2 }}>
                <LeadTimeChart monthlyData={data.monthlyAggregates} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <LeadTimeStatistics statistics={data.statistics} />
              </Box>
            </Box>
            <LeadTimeDataTable records={data.records} />
          </>
        )}

        {!data && !isFetching && (
          <Typography variant="body1" color="textSecondary">
            {t('leadTime.noData')}
          </Typography>
        )}
      </PageContainer>
    </>
  )
}

export default LeadTimePage
